import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const RADICAL_CANDOR_PACK: FrameworkPackDefinition = {
  id: "radical_candor",
  label: "Radical Candor",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Radical Candor notices the balance between care personally and challenge directly, especially in feedback, disagreement, and clarity moments.",
    whatItNotices: [
      "Where feedback or disagreement was softened, delayed, or left unnamed.",
      "Whether care and direct challenge were both present in the moment.",
      "Where clarity, kindness, or relational trust shaped how truth was delivered.",
    ],
    watchouts: [
      "Do not treat bluntness as automatically better leadership.",
      "Do not infer conflict avoidance, people-pleasing, or fear from a single moment.",
      "Do not prescribe directness without context, timing, and care.",
    ],
    sampleCoachLanguage: [
      "What was clear here, and what stayed softened?",
      "Where did care and challenge both show up, and where was one missing?",
    ],
    sampleLeaderLanguage: [
      "Through this lens, this moment may highlight the balance between care and direct challenge.",
      "A useful reflection is what clarity remained unsaid while the relationship was being protected.",
    ],
  },
  runtime: {
    coreConstructs: ["Care personally", "Challenge directly", "Feedback", "Clarity", "Relational trust"],
    diagnosticQuestions: [
      "What was said directly, and what remained softened?",
      "Where were care and challenge both visible in the exchange?",
      "What truth needed clearer language without reducing care?",
    ],
    interventionPatterns: [
      "Name the care-challenge balance.",
      "Separate clarity from harshness.",
      "Identify the specific feedback or disagreement moment.",
    ],
    progressMarkers: [
      "The leader names what was clear and what was left vague.",
      "Feedback becomes more specific while relational care remains visible.",
      "Disagreement can be surfaced without collapsing trust.",
    ],
    stallSignals: [
      "The conversation protects harmony while leaving the core issue unnamed.",
      "Directness appears without enough relational context or care.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "softened_challenge",
        label: "Softened challenge",
        description: "A feedback or disagreement moment appeared but was softened or moved past.",
        positiveIndicators: ["disagreement", "feedback", "concern", "pushback", "challenge"],
        negativeIndicators: ["moved on", "softened", "maybe", "generally aligned", "later"],
        evidenceHints: ["disagreement surfaced", "moved past quickly", "did not explore", "revisit later"],
        prohibitedInferences: ["conflict avoidance", "people pleasing", "fear of confrontation"],
      },
      {
        id: "care_without_clarity",
        label: "Care without clarity",
        description: "Relational protection may have reduced specificity or directness.",
        positiveIndicators: ["support", "care", "relationship", "alignment"],
        negativeIndicators: ["unclear", "vague", "not specific", "soft"],
        evidenceHints: ["wanted to preserve", "keep alignment", "avoid tense"],
        prohibitedInferences: ["insecurity", "avoidance", "low courage"],
      },
    ],
    reflectionPatterns: [
      {
        id: "care_challenge_balance",
        label: "Care and challenge balance",
        promptTemplates: [
          "What was said directly in this moment, and what remained softened?",
          "Where were care and challenge both present here?",
          "What clarity might have helped without reducing care?",
        ],
        useWhen: ["softened_challenge", "care_without_clarity"],
        avoidWhen: ["thin_evidence", "sensitive_power_dynamic"],
      },
    ],
    boundaries: {
      allowed: ["direct challenge", "care", "clarity", "softened disagreement", "feedback moment"],
      prohibited: ["you avoid conflict", "people pleasing", "fear of confrontation", "you should be more direct"],
      coachOnly: ["interpret repeated conflict patterns", "coach feedback delivery", "challenge avoidance with context"],
    },
    salienceWeights: {
      softened_challenge: 1.5,
      care_without_clarity: 1.2,
    },
  },
};
